import React from 'react';
import {
  Box, Typography, Stack, TextField, Button, IconButton, Divider, Link as MuiLink,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import PhoneFrame, { AutomatrixBanner, NavBar } from './PhoneFrame';
import { STUDENT } from '../sharedMockData';

/**
 * Layer 1 from ACHIEVEDXP_ANDROID_DESIGN.md §1a — AchieveDXP dashboard login
 * rendered inside the Android app's parent WebView. User types their
 * AchieveDXP credentials directly. No backend auth work.
 */
const AndroidLoginMockup = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', py: 2 }}>
    <PhoneFrame background="#fafbfe">
      <AutomatrixBanner />

      {/* Fake WebView url bar to signal "this is a WebView" */}
      <Box sx={{ bgcolor: '#eceff5', px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" sx={{ p: 0.25 }}><ArrowBackIcon sx={{ fontSize: 16 }} /></IconButton>
        <Box sx={{
          flexGrow: 1, bgcolor: '#fff', borderRadius: 16, px: 1.25, py: 0.5,
          display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid #dde0e7',
        }}>
          <LockIcon sx={{ fontSize: 11, color: '#2e7d32' }} />
          <Typography sx={{ fontSize: '0.65rem', fontFamily: 'monospace', color: '#5f6368' }}>
            achievedxp.nucleos.com/login
          </Typography>
        </Box>
        <IconButton size="small" sx={{ p: 0.25 }}><RefreshIcon sx={{ fontSize: 16 }} /></IconButton>
        <IconButton size="small" sx={{ p: 0.25 }}><MoreIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>

      {/* Page content (web login, rendered in WebView) */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', px: 3, pt: 4, display: 'flex', flexDirection: 'column' }}>
        {/* AchieveDXP logo mark */}
        <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(25,118,210,0.25)',
          }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, fontFamily: 'system-ui' }}>A</Typography>
          </Box>
          <Typography variant="h6" fontWeight={800} sx={{ mt: 1, color: '#0d47a1' }}>
            AchieveDXP
          </Typography>
          <Typography variant="caption" sx={{ color: '#606770' }}>
            Sign in to continue
          </Typography>
        </Stack>

        <Stack spacing={2}>
          <TextField
            label="Email or username"
            size="small"
            fullWidth
            defaultValue={STUDENT.email}
            variant="outlined"
            InputProps={{ sx: { borderRadius: 2 } }}
          />
          <TextField
            label="Password"
            size="small"
            type="password"
            fullWidth
            defaultValue="••••••••••"
            variant="outlined"
            InputProps={{ sx: { borderRadius: 2 } }}
          />

          <Button
            variant="contained"
            size="large"
            sx={{
              borderRadius: 28,
              textTransform: 'none',
              fontWeight: 700,
              py: 1.1,
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
            }}
          >
            Sign In
          </Button>

          <MuiLink
            underline="hover"
            sx={{ textAlign: 'center', fontSize: '0.8rem', color: '#1976d2', cursor: 'pointer' }}
          >
            Forgot password?
          </MuiLink>

          <Divider sx={{ my: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              or
            </Typography>
          </Divider>

          <Button
            variant="outlined"
            size="medium"
            sx={{
              borderRadius: 28,
              textTransform: 'none',
              fontWeight: 600,
              color: '#1f1f1f',
              borderColor: '#dadce0',
            }}
            startIcon={
              <Box sx={{
                width: 16, height: 16, borderRadius: '50%',
                background: 'conic-gradient(#4285F4 0deg 90deg, #34A853 90deg 180deg, #FBBC05 180deg 270deg, #EA4335 270deg 360deg)',
              }} />
            }
          >
            Continue with Google
          </Button>
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <Typography
          variant="caption"
          align="center"
          sx={{ color: '#8a8d93', fontSize: '0.65rem', mt: 3, mb: 1 }}
        >
          Secured by Nucleos · Facility device enrolled via MDM
        </Typography>
      </Box>

      <NavBar label="Sign in to AchieveDXP" />
    </PhoneFrame>
  </Box>
);

export default AndroidLoginMockup;
